import React, { useState, useEffect } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import TodoList from "../components/TodoList";
import { FaSpinner } from "react-icons/fa";

const TodoListPage = () => {
  const { user, authStatus } = useAuthenticator((context) => [
    context.user,
    context.authStatus,
  ]);
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("general");
  const [loading, setLoading] = useState(true);

  // Define the available chatbots (same as in ChatInterface)
  const subscribedProducts = ["0", "1"]; // Jarvis and Samantha

  // Handler for chatbot selection (redirects to chat interface)
  const handleChatBotSelection = (botId, threadId = null) => {
    // Save the selected thread to localStorage before navigating
    if (botId && threadId) {
      localStorage.setItem(
        "lastVisitedThread",
        JSON.stringify({ botId, threadId })
      );
    }
    // Navigate to the chat interface
    navigate("/myai");
  };

  // Define the available categories
  const categories = [
    { id: "general", name: "General" },
    { id: "work", name: "Work" },
    { id: "personal", name: "Personal" },
  ];

  // Check authentication status
  useEffect(() => {
    if (authStatus === "authenticated" && user) {
      setLoading(false);
    } else if (authStatus === "unauthenticated") {
      // Redirect to login if not authenticated
      navigate("/");
    } else if (authStatus !== "configuring") {
      // If not loading auth and not authenticated, set loading to false
      setLoading(false);
    }
  }, [authStatus, user, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-900 text-white">
        <FaSpinner className="animate-spin text-blue-500 text-2xl mr-2" />
        <span>Loading...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-900 text-white">
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <p className="text-xl mb-4">Please sign in to view your todo lists</p>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-900 text-white">
      <Sidebar
        user={user}
        subscribedProducts={subscribedProducts}
        setSelectedChatBotId={handleChatBotSelection}
      />
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-6">Todo Lists</h1>

          <div className="mb-6">
            <div className="flex space-x-2 mb-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    selectedCategory === category.id
                      ? "bg-blue-600 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          <TodoList todoCategory={selectedCategory} />

          <div className="mt-8 bg-gray-800 bg-opacity-50 border border-gray-700 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-blue-300 mb-4">
              How to Use
            </h2>
            <p className="text-gray-300 mb-4">
              Your todo list is automatically updated when you chat with the AI
              assistant. Try asking the assistant to:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Add a new task to my todo list</li>
              <li>Create a todo for me to finish the project by Friday</li>
              <li>Mark my task about the presentation as done</li>
              <li>Update my todo list with a new work item</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodoListPage;
